/**
 * Form a11y kit — shared constants & helpers (no Drupal.behaviors).
 *
 * Independent from GLightbox: no shared classes, data-* marks, or imports.
 *
 * Toast selectors (edit here only):
 *   [data-ssks-message-type="status"|"error"] on the toast root
 *   [data-ssks-message-dismiss] on the dismiss control
 * Fallbacks: Bootstrap .alert-success/.alert-danger, Drupal .messages--*.
 */

export const SUMMARY_CLASS = "ssks-form-error-summary";
export const FEEDBACK_CLASS = "ssks-form-feedback";

export const TOAST_HANDLED_CLASS = "ssks-messages--form-errors-handled";
export const STATUS_HANDLED_CLASS = "ssks-messages--form-status-handled";

/** Dismiss control inside a toast (Twig mark first, Bootstrap class fallback). */
export const TOAST_DISMISS_SELECTOR =
  "[data-ssks-message-dismiss], .btn-close";

export const SUCCESS_TOAST_SELECTOR = [
  '[data-drupal-messages] [data-ssks-message-type="status"]',
  "[data-drupal-messages] .alert-success",
  "[data-drupal-messages] .messages--status",
  ".messages.messages--status",
].join(", ");

export const ERROR_TOAST_SELECTOR = [
  '[data-drupal-messages] [data-ssks-message-type="error"]',
  "[data-drupal-messages] .alert-danger",
  "[data-drupal-messages] .messages--error",
  ".messages.messages--error",
].join(", ");

/**
 * @param {"error"|"status"} type
 * @return {string}
 */
export function toastInnerSelector(type) {
  return type === "status"
    ? '[data-ssks-message-type="status"], .alert-success, .messages--status'
    : '[data-ssks-message-type="error"], .alert-danger, .messages--error';
}

export const FIELD_ERROR_SELECTOR =
  "form .form-item--error-message, form [id$='--error']";

/** Hôte du feedback webform : la confirmation d'abord, sinon le formulaire. */
const WEBFORM_CONFIRMATION_SELECTOR = ".webform-confirmation";
const WEBFORM_FORM_SELECTOR =
  "form.webform-submission-form, form[data-drupal-selector*='webform']";

/**
 * @param {string} string
 * @param {Object<string, string>} [args]
 * @return {string}
 */
export function translate(string, args) {
  if (typeof Drupal !== "undefined" && typeof Drupal.t === "function") {
    return Drupal.t(string, args);
  }
  if (!args) {
    return string;
  }
  return Object.keys(args).reduce(
    (out, key) => out.split(key).join(String(args[key])),
    string
  );
}

/** @param {HTMLElement} el */
export function focusPreventScroll(el) {
  if (!(el instanceof HTMLElement)) {
    return;
  }
  el.focus({ preventScroll: true });
}

export function isSafeDomId(id) {
  return typeof id === "string" && /^[A-Za-z][\w:.-]*$/.test(id);
}

/**
 * @param {unknown} text
 * @return {string}
 */
export function sanitizeDomText(text) {
  return String(text || "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {string} id
 * @return {string} "#id" or "" if unsafe
 */
export function safeHashHref(id) {
  return isSafeDomId(id) ? `#${id}` : "";
}

/**
 * @param {unknown} value
 * @param {string} [fallback]
 * @return {string}
 */
export function safeDomToken(value, fallback = "form") {
  const cleaned = String(value || fallback).replace(/[^a-zA-Z0-9_-]/g, "-");
  return cleaned || fallback;
}

export function lang() {
  return document.documentElement.lang || "fr";
}

export function ensureFocusable(el) {
  if (
    el.matches(
      "a[href], button, input, select, textarea, summary, [contenteditable]"
    )
  ) {
    el.removeAttribute("tabindex");
    return;
  }
  if (!el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "-1");
  }
}

/** @type {{ at: number, value: number } | null} */
let stickyInsetCache = null;

export function stickyInsetPx() {
  const now = performance.now();
  if (stickyInsetCache && now - stickyInsetCache.at < 32) {
    return stickyInsetCache.value;
  }

  let inset = 0;
  [
    "#ssks-alert-landing-zone",
    "header.site-header",
    "#toolbar-bar",
    ".toolbar-oriented",
  ].forEach((sel) => {
    const el = document.querySelector(sel);
    if (!(el instanceof HTMLElement)) {
      return;
    }
    const style = window.getComputedStyle(el);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      (style.position !== "sticky" && style.position !== "fixed")
    ) {
      return;
    }
    const rect = el.getBoundingClientRect();
    if (rect.height > 0 && rect.bottom > inset) {
      inset = rect.bottom;
    }
  });
  const value = Math.ceil(inset) + 8;
  stickyInsetCache = { at: now, value };
  return value;
}

export function scrollBelowSticky(el, behavior = "auto") {
  const top =
    window.scrollY + el.getBoundingClientRect().top - stickyInsetPx();
  window.scrollTo({ top: Math.max(0, top), behavior });
}

/**
 * Reclaim focus if submit or a toast stole it. Skip if already on `el`
 * (re-focusing interrupts screen-reader speech).
 *
 * @param {HTMLElement} el
 */
export function claimFocus(el) {
  if (!(el instanceof HTMLElement)) {
    return;
  }
  const run = () => {
    if (!el.isConnected) {
      return;
    }
    const active = document.activeElement;
    if (active === el) {
      return;
    }
    if (
      active instanceof HTMLElement &&
      (active.closest("[data-drupal-messages]") ||
        active.closest(".skip-link-container") ||
        active.classList.contains("skip-link"))
    ) {
      active.blur();
    }
    focusPreventScroll(el);
  };
  run();
  window.requestAnimationFrame(run);
  [50, 150, 350].forEach((ms) => {
    window.setTimeout(run, ms);
  });
}

/**
 * Hide or restore Drupal toasts of one type. Hiding also strips live/alert
 * so the toast we replace is not announced.
 *
 * @param {"error"|"status"} type
 * @param {boolean} hide
 */
export function syncToastVisibility(type, hide) {
  const handledClass =
    type === "status" ? STATUS_HANDLED_CLASS : TOAST_HANDLED_CLASS;
  const toastSel = toastInnerSelector(type);

  document.querySelectorAll("[data-drupal-messages]").forEach((wrapper) => {
    if (hide) {
      wrapper.classList.add(handledClass);
      wrapper.querySelectorAll(toastSel).forEach((toast) => {
        toast.setAttribute("hidden", "hidden");
        toast.setAttribute("aria-hidden", "true");
        if ("inert" in toast) {
          toast.inert = true;
        }
        toast.querySelectorAll('[role="alert"]').forEach((alert) => {
          alert.removeAttribute("role");
        });
        toast.removeAttribute("aria-live");
        toast.removeAttribute("role");
        toast.removeAttribute("aria-label");
        toast.querySelectorAll(TOAST_DISMISS_SELECTOR).forEach((btn) => {
          btn.setAttribute("hidden", "hidden");
          btn.setAttribute("tabindex", "-1");
          if (document.activeElement === btn) {
            btn.blur();
          }
        });
      });
    } else {
      wrapper.classList.remove(handledClass);
      wrapper.querySelectorAll(toastSel).forEach((toast) => {
        toast.removeAttribute("hidden");
        toast.removeAttribute("aria-hidden");
        if ("inert" in toast) {
          toast.inert = false;
        }
        // Match status-messages.html.twig defaults.
        toast.setAttribute("aria-live", "polite");
        if (type === "error") {
          const inner = toast.querySelector(":scope > div");
          if (
            inner instanceof HTMLElement &&
            !inner.matches(TOAST_DISMISS_SELECTOR)
          ) {
            inner.setAttribute("role", "alert");
          }
        }
      toast.querySelectorAll(TOAST_DISMISS_SELECTOR).forEach((btn) => {
        btn.removeAttribute("hidden");
        // Toast visible = bouton fermer de nouveau accessible au clavier.
        btn.removeAttribute("tabindex");
      });
      });
    }
  });
}

/** Mute status + error toasts before relocating them under a webform. */
export function muteRelocatableToasts() {
  syncToastVisibility("status", true);
  syncToastVisibility("error", true);
}

/**
 * @param {() => void} fn
 * @param {number} [delay]
 * @return {() => void}
 */
export function createScheduler(fn, delay = 50) {
  let timer = null;
  return () => {
    if (timer) {
      window.clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      timer = null;
      fn();
    }, delay);
  };
}

export function readToastText(toast) {
  if (!(toast instanceof HTMLElement)) {
    return "";
  }
  const clone = toast.cloneNode(true);
  clone
    .querySelectorAll(
      `${TOAST_DISMISS_SELECTOR}, .visually-hidden, .sr-only`
    )
    .forEach((el) => el.remove());
  return sanitizeDomText(clone.textContent);
}

/**
 * Formulaire envoyé sans AJAX (newsletter…) : son id est noté à l'envoi,
 * puis relu une seule fois au chargement suivant pour y placer le message.
 */
const SUBMITTED_FORM_KEY = "ssks-form-a11y-submitted";

/** Id sans le suffixe Drupal "--xxx" ajouté aux reconstructions. */
function baseFormId(id) {
  return id.replace(/--[\w-]+$/, "");
}

/**
 * Lu au premier appel seulement : chaque bundle embarque sa copie de ce
 * module, et seul form-webform-feedback.js appelle findWebformFeedbackHost().
 *
 * @type {string|null|undefined}
 */
let submittedFormId;

function readSubmittedFormId() {
  if (submittedFormId === undefined) {
    try {
      submittedFormId = window.sessionStorage.getItem(SUBMITTED_FORM_KEY);
      window.sessionStorage.removeItem(SUBMITTED_FORM_KEY);
    } catch (e) {
      submittedFormId = null;
    }
  }
  return submittedFormId;
}

/**
 * @param {HTMLFormElement} form
 */
export function rememberSubmittedForm(form) {
  if (!form.id || !isSafeDomId(form.id)) {
    return;
  }
  try {
    window.sessionStorage.setItem(SUBMITTED_FORM_KEY, baseFormId(form.id));
  } catch (e) {
    // Stockage indisponible : le toast reste en place.
  }
}

export function findWebformFeedbackHost() {
  const confirmation = document.querySelector(WEBFORM_CONFIRMATION_SELECTOR);
  if (confirmation instanceof HTMLElement) {
    return confirmation;
  }
  const form = document.querySelector(WEBFORM_FORM_SELECTOR);
  if (form instanceof HTMLElement) {
    return form;
  }
  const submittedId = readSubmittedFormId();
  if (!submittedId) {
    return null;
  }
  const submitted = [...document.forms].find(
    (f) => f.id && baseFormId(f.id) === submittedId
  );
  return submitted || null;
}
